import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabaseRpc } from '@/lib/supabaseClient'

export interface InviteCodeRow {
  id: string
  code: string
  months: number
  note: string | null
  expires_at: string | null
  redeemed_at: string | null
  redeemed_by_org_name: string | null
  redeemed_by_email: string | null
  created_at: string
  state: 'available' | 'redeemed' | 'expired'
}

export interface ActiveProGrant {
  source: 'invite_code' | 'founder' | 'manual'
  months: number | null
  starts_at: string | null
  expires_at: string | null
}

export interface RedeemResult {
  tier: 'pro'
  months: number
  expires_at: string
}

// Server-side super-admin check (platform_admins). Drives admin-only UI.
export function useIsPlatformAdminQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['is-platform-admin'],
    queryFn: () => supabaseRpc<boolean>('is_platform_admin'),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function useInviteCodesQuery(enabled: boolean = true) {
  return useQuery({
    queryKey: ['invite-codes'],
    queryFn: () => supabaseRpc<InviteCodeRow[]>('admin_list_invite_codes'),
    enabled,
  })
}

export interface CreateInviteCodeParams {
  months: number
  note?: string
  expiresAt?: string | null
}

export function useCreateInviteCodeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (params: CreateInviteCodeParams) =>
      supabaseRpc<InviteCodeRow>('admin_create_invite_code', {
        p_months: params.months,
        p_note: params.note ?? null,
        p_expires_at: params.expiresAt ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invite-codes'] })
    },
  })
}

export function useRevokeInviteCodeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => supabaseRpc('admin_revoke_invite_code', { p_code_id: id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invite-codes'] })
    },
  })
}

export function useRedeemInviteCodeMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) => supabaseRpc<RedeemResult>('redeem_invite_code', { p_code: code }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subscription'] })
      void qc.invalidateQueries({ queryKey: ['active-pro-grant'] })
    },
  })
}

export function useActiveProGrantQuery(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['active-pro-grant', organizationId],
    queryFn: () => supabaseRpc<ActiveProGrant | null>('get_active_pro_grant'),
    enabled: !!organizationId,
    staleTime: 30_000,
  })
}
